/**
 * The platform badge beside the wordmark.
 *
 * "for macOS", with the Apple mark arriving in front of it. It is the cheapest
 * honest claim this page can make about itself: two native apps, no Electron, no
 * WebKit, and the reader is holding a Mac to read it at all.
 *
 * The mark is Apple's own, carried from Simple Icons, because a brand asset is not
 * something to redraw from memory and then ship as if it were the real thing.
 *
 * Not a link and not a chip. It is metadata, so it wears metadata's weight: a
 * hairline separates it from the wordmark and the whole thing is muted. The header
 * already spends its two pill shapes on "Entrar" and the menu button, and a third
 * rounded thing here would be decoration rather than information.
 *
 * Hidden below `sm`, where the header's own navigation is hidden too. At 360px the
 * badge, the wordmark and "Entrar" already fill the width, and the platform is
 * stated in the hero copy and again in the requirements line further down.
 */

import React from 'react';

import { useI18n } from '../i18n';

/*
  Simple Icons' Apple mark at 24x24. Verbatim, not approximated: this path sits
  directly beside the product's own name, and a hand-traced Apple logo is a small
  lie in a place where the reader is already looking closely.
*/
const APPLE_MARK =
  'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701';

export const ForMacOS: React.FC = () => {
  const { locale, t } = useI18n();

  return (
    <span className="hidden items-center gap-2.5 sm:flex">
      {/* A hairline rather than a gap: it says these are two separate facts, not one word. */}
      <span aria-hidden="true" className="h-3.5 w-px bg-line-strong" />

      <span lang={locale} className="flex items-center gap-1.5 text-[12px] font-medium tracking-tight text-muted">
        {/*
          The mark is `aria-hidden` rather than labelled: the words beside it already
          name the platform, so announcing the logo too would say it twice.
        */}
        <svg
          viewBox="0 0 24 24"
          className="apple-badge-mark size-[13px] shrink-0"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <path d={APPLE_MARK} />
        </svg>

        <span className="apple-badge-label">{t.chrome.platformBadge}</span>
      </span>
    </span>
  );
};